import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Backdrop from '../Backdrop';

const SideDrawer = ({ children, direction, isOpen, onDismiss }) => {
  const touchStartXRef = useRef(null);
  const [dragOffset, setDragOffset] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const closeThreshold = 56;

  useEffect(() => {
    if (!isOpen) {
      setDragOffset(null);
      setIsDragging(false);
    }
  }, [isOpen]);

  const handleTouchStart = (event) => {
    if (!isOpen) return;
    touchStartXRef.current = event.touches[0]?.clientX ?? null;
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleTouchMove = (event) => {
    if (!isOpen || touchStartXRef.current === null) return;
    const currentX = event.touches[0]?.clientX ?? touchStartXRef.current;
    const deltaX = currentX - touchStartXRef.current;

    if (direction === 'left') {
      setDragOffset(Math.min(0, deltaX));
      return;
    }

    setDragOffset(Math.max(0, deltaX));
  };

  const handleTouchEnd = () => {
    if (!isOpen || touchStartXRef.current === null) return;

    const currentOffset = Number(dragOffset || 0);
    const shouldDismiss =
      direction === 'left'
        ? currentOffset <= -closeThreshold
        : currentOffset >= closeThreshold;

    touchStartXRef.current = null;
    setIsDragging(false);
    setDragOffset(null);

    if (shouldDismiss) {
      onDismiss();
    }
  };

  return (
    <div>
      <Backdrop backdropVisible={isOpen} hideBackdrop={onDismiss} />
      <div
        className={`max-w-xs w-full h-full overflow-scroll fixed top-0 z-[500] bg-surface ease-in touch-pan-y ${
          direction === 'right'
            ? 'right-0 translate-x-full'
            : 'left-0 -translate-x-full'
        } ${isOpen && 'translate-x-0'} ${
          isDragging ? 'transition-none' : 'transition-transform duration-300'
        }`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={
          dragOffset === null
            ? undefined
            : {
                transform: `translateX(${dragOffset}px)`,
              }
        }
      >
        {children}
      </div>
    </div>
  );
};

SideDrawer.propTypes = {
  children: PropTypes.element.isRequired,
  direction: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default SideDrawer;
